import { useState } from "react";
import {
  createDraft,
  draftToChanges,
  normalizeNumberInput,
} from "../components/IndicatorSettingsModal/indicatorDraft";

/**
 * Inputs and styles of an indicator edited in its settings modal (TV-17).
 * The chart does not change until Save: the draft lives only while the
 * modal is open, so Cancel just drops it.
 *
 * @param {object} indicator - engine.getIndicators() item
 * @param {object} definition - from the registry
 * @returns {{
 *   draft: { inputs: object, plots: object, levels: object[] },
 *   setInput: (key: string, value: string) => void,
 *   commitInput: (input: object) => void,
 *   setPlot: (key: string, change: object) => void,
 *   setLevel: (index: number, change: object) => void,
 *   getChanges: () => { inputs: object, styles: object },
 * }}
 */
const useIndicatorDraft = (indicator, definition) => {
  const [draft, setDraft] = useState(() => createDraft(indicator, definition));

  const setInput = (key, value) => {
    setDraft((current) => ({ ...current, inputs: { ...current.inputs, [key]: value } }));
  };

  // A number field shows what will be saved once it loses focus
  const commitInput = (input) => {
    setDraft((current) => {
      const value = normalizeNumberInput(input, current.inputs[input.key], indicator.inputs[input.key]);

      return { ...current, inputs: { ...current.inputs, [input.key]: String(value) } };
    });
  };

  const setPlot = (key, change) => {
    setDraft((current) => ({
      ...current,
      plots: { ...current.plots, [key]: { ...current.plots[key], ...change } },
    }));
  };

  const setLevel = (index, change) => {
    setDraft((current) => ({
      ...current,
      levels: current.levels.map((level, levelIndex) => (levelIndex === index ? { ...level, ...change } : level)),
    }));
  };

  const getChanges = () => draftToChanges(draft, indicator, definition);

  return { draft, setInput, commitInput, setPlot, setLevel, getChanges };
};

export default useIndicatorDraft;
